import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../queryKeys';
import { suppliersApi, QuerySupplierParams, CreateSupplierDto } from '../services/suppliers.api';
import { toast } from 'sonner';
import { getErrorMessage } from '../client';

/**
 * Query hook for fetching paginated suppliers list
 */
export function useSuppliersList(params: QuerySupplierParams = {}) {
  return useQuery({
    queryKey: queryKeys.suppliers.list(params),
    queryFn: () => suppliersApi.getAll(params),
  });
}

/**
 * Query hook for fetching a single supplier
 */
export function useSupplier(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.suppliers.detail(id || ''),
    queryFn: () => suppliersApi.getById(id!),
    enabled: !!id,
  });
}

/**
 * Query hook for fetching supplier performance metrics
 */
export function useSupplierPerformance(id: string | undefined) {
  return useQuery({
    queryKey: [...queryKeys.suppliers.detail(id || ''), 'performance'],
    queryFn: () => suppliersApi.getPerformance(id!),
    enabled: !!id,
  });
}

/**
 * Mutation hook for creating a supplier
 */
export function useCreateSupplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateSupplierDto) => suppliersApi.create(data),
    onSuccess: () => {
      toast.success('Supplier created successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.lists() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for updating a supplier
 */
export function useUpdateSupplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CreateSupplierDto> }) =>
      suppliersApi.update(id, data),
    onSuccess: (_, variables) => {
      toast.success('Supplier updated successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.detail(variables.id) });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}

/**
 * Mutation hook for deleting a supplier
 */
export function useDeleteSupplier() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => suppliersApi.delete(id),
    onSuccess: () => {
      toast.success('Supplier deleted successfully');
      queryClient.invalidateQueries({ queryKey: queryKeys.suppliers.lists() });
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });
}
